import { Component, inject, signal } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { LoadService } from '../../../../core/services/load.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';

@Component({
  selector: 'app-email-documents-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    InputComponent
],
  templateUrl: './email-documents-modal.component.html',
})
export class EmailDocumentsModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);

  protected readonly load: Load = this.modalService.state()!.data.load;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      email: [
        this.authService.user()?.email ?? '',
        [Validators.required, Validators.email],
      ],
    });
  }

  cancel(): void {
    this.modalService.close();
  }

  async send(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const email = (this.form.getRawValue().email as string).trim();
    this.isSaving.set(true);
    this.loadingService.show({
      variant: 'overlay',
      title: 'Emailing Documents',
      subtitle: `Sending all load documents to ${email}...`,
    });

    try {
      const res = await this.loadService.emailLoadDocuments(this.load.id, email);
      this.loadingService.hide();
      this.modalService.close();
      const note = res.oversized
        ? ' Some large files were included as download links.'
        : '';
      setTimeout(() => {
        this.toastService.show(
          'success',
          `Sent ${res.attachedCount} document(s) to ${res.sentTo}.${note}`,
          { title: 'Documents Emailed', duration: 5000 }
        );
      }, 100);
    } catch (error: any) {
      this.loadingService.hide();
      let msg = 'Failed to email documents. Please try again.';
      if (error?.code === 'functions/failed-precondition') {
        msg = error.message;
      } else if (error?.message) {
        msg = error.message;
      }
      this.toastService.show('error', msg, {
        title: 'Email Failed',
        showClose: true,
        duration: 0,
      });
    } finally {
      this.isSaving.set(false);
    }
  }
}
